import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Col, Row} from 'react-bootstrap';
import Journal from '@mui/icons-material/AssignmentRounded';
import {Cell, Pie, PieChart, ResponsiveContainer} from 'recharts';
import {useHistory} from 'react-router-dom';
import {isMonitoredLifecycle, isEdgeHealthy, isEdgeOffline, isEdgeUnhealthy, lifecycleConfig} from '../constants/edgesConstants';
import '../styles/widgets.css';

const StatsWidgets = ({edges, filterStatus}) => {
  const history = useHistory();

  const activeEdgesCount = edges.filter(
    (e) => isMonitoredLifecycle(e.lifecycle?.status) && isEdgeHealthy(e)
  ).length;

  const issueEdgesCount = edges.filter(
    (e) => isMonitoredLifecycle(e.lifecycle?.status) && isEdgeOffline(e)
  ).length;

  const unhealthyEdgesCount = edges.filter(
    (e) => isMonitoredLifecycle(e.lifecycle?.status) && isEdgeUnhealthy(e)
  ).length;

  const pieData = useMemo(() => {
    const stats = edges.reduce((acc, edge) => {
      const status = edge.lifecycle?.status;
      if (status && lifecycleConfig[status]) {
        acc[status] = (acc[status] || 0) + 1;
      }
      return acc;
    }, {});
    return Object.entries(lifecycleConfig)
      .map(([key, config]) => ({name: config.label, value: stats[key] || 0, color: config.color}))
      .filter((d) => d.value > 0);
  }, [edges]);

  return (
    <Row className="mb-4 g-3">
      <Col md={3}>
        <button
          type="button"
          className={`p-4 widget-island h-100 w-100 position-relative widget-button${filterStatus.includes('active') ? ' widget-button-active' : ''}`}
          style={{backgroundColor: 'var(--blue)', color: 'var(--text-badge)', minHeight: '160px'}}
          onClick={() => history.push(filterStatus.includes('active') ? '/' : '/?status=active')}
        >
          <div className="mb-2">Healthy SCCs</div>
          <div className="d-flex align-items-center mb-3">
            <h2 className="mb-0 fw-bold me-2" style={{color: 'var(--text-badge)'}}>{activeEdgesCount} Edges</h2>
          </div>
          <div style={{fontSize: '1.2rem', maxWidth: '85%'}}>are up and running without issues.</div>
          <div className="position-absolute" style={{top: '24px', right: '24px', opacity: 0.8}}>
            <Journal size={32}/>
          </div>
        </button>
      </Col>
      <Col md={3}>
        <button
          type="button"
          className={`p-4 widget-island h-100 w-100 position-relative widget-button${filterStatus.includes('unhealthy') ? ' widget-button-active' : ''}`}
          style={{backgroundColor: 'var(--yellow)', color: 'var(--text-badge)', minHeight: '160px'}}
          onClick={() => history.push(filterStatus.includes('unhealthy') ? '/' : '/?status=unhealthy')}
        >
          <div className="mb-2">Unhealthy SCCs</div>
          <div className="d-flex align-items-center mb-3">
            <h2 className="mb-0 fw-bold me-2" style={{color: 'var(--text-badge)'}}>{unhealthyEdgesCount} Edges</h2>
          </div>
          <div style={{fontSize: '1.2rem', maxWidth: '85%'}}>are online but emitting recurring errors.</div>
          <div className="position-absolute" style={{top: '24px', right: '24px', opacity: 0.8}}>
            <Journal size={32}/>
          </div>
        </button>
      </Col>
      <Col md={3}>
        <button
          type="button"
          className={`p-4 widget-island h-100 w-100 position-relative widget-button${filterStatus.includes('issue') ? ' widget-button-active' : ''}`}
          style={{backgroundColor: 'var(--red)', color: 'var(--text-badge)', minHeight: '160px'}}
          onClick={() => history.push(filterStatus.includes('issue') ? '/' : '/?status=issue')}
        >
          <div className="mb-2">Offline SCCs</div>
          <div className="d-flex align-items-center mb-3">
            <h2 className="mb-0 fw-bold me-2" style={{color: 'var(--text-badge)'}}>{issueEdgesCount} Edges</h2>
          </div>
          <div style={{fontSize: '1.2rem', maxWidth: '85%'}}>are not sending any data and need attention.</div>
          <div className="position-absolute" style={{top: '24px', right: '24px', opacity: 0.8}}>
            <Journal size={32}/>
          </div>
        </button>
      </Col>
      <Col md={3}>
        <div
          className="p-3 widget-island h-100 d-flex flex-column widget-button"
          style={{backgroundColor: 'var(--island-bg)', color: 'var(--text-primary)'}}
        >
          <h6 className="mb-2">Your SCCs - Lifecycle</h6>
          <div className="flex-grow-1 d-flex align-items-center">
            <div style={{width: '40%', height: '120px'}}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius="50%"
                    outerRadius="100%"
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color}/>
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{width: '50%'}}>
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '0.75rem'}}>
                {pieData.map((entry) => (
                  <div key={entry.name} className="d-flex justify-content-between mb-1 align-items-center">
                    <div className="d-flex align-items-center text-truncate">
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: entry.color,
                          marginRight: 6,
                          flexShrink: 0,
                        }}
                      />
                      <span className="text-truncate" title={entry.name}>{entry.name}</span>
                    </div>
                    <span className="ms-2 fw-bold">
                      {entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};

StatsWidgets.propTypes = {
  edges: PropTypes.array.isRequired,
  filterStatus: PropTypes.array.isRequired,
};

export default StatsWidgets;
